import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2, Power, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  SdTestAppBenchClient,
  type BenchSettingsAbout,
  type BenchSettingsField,
} from "@/lib/sdtestapp/sdTestAppBenchClient";

export function BenchSettingsTab({
  port,
  machineId,
  canActuate,
  observeOnly = false,
}: {
  port: string;
  machineId?: string;
  canActuate: boolean;
  observeOnly?: boolean;
}): JSX.Element {
  const client = useMemo(() => new SdTestAppBenchClient(port), [port]);
  const [fields, setFields] = useState<BenchSettingsField[]>([]);
  const [about, setAbout] = useState<BenchSettingsAbout | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [pendingField, setPendingField] = useState<string | null>(null);
  const [exitPending, setExitPending] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [updateCheckPending, setUpdateCheckPending] = useState(false);
  const [updateCheckMessage, setUpdateCheckMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [rows, aboutInfo] = await Promise.all([
          observeOnly && machineId
            ? SdTestAppBenchClient.relaySettings(machineId)
            : client.fetchSettings(),
          observeOnly && machineId
            ? SdTestAppBenchClient.relaySettingsAbout(machineId)
            : client.fetchSettingsAbout(),
        ]);
        setFields(rows);
        setAbout(aboutInfo);
        const nextDrafts: Record<string, string> = {};
        for (const field of rows) {
          nextDrafts[field.name] = field.value == null ? "" : String(field.value);
        }
        setDrafts(nextDrafts);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : String(loadError));
      }
    };
    if (observeOnly && machineId) {
      void load();
      return undefined;
    }
    if (!canActuate) return undefined;
    void load();
    return undefined;
  }, [canActuate, client, machineId, observeOnly]);

  const sections = useMemo(() => {
    const grouped = new Map<string, BenchSettingsField[]>();
    for (const field of fields) {
      const section = field.section || "General";
      const list = grouped.get(section) ?? [];
      list.push(field);
      grouped.set(section, list);
    }
    return Array.from(grouped.entries());
  }, [fields]);

  return (
    <div className="space-y-4">
      <section className="rounded border p-4">
        <h3 className="mb-2 font-semibold">Application</h3>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">Version</span>
          <span className="font-mono">{about?.version ? `v${about.version}` : "—"}</span>
          {about?.releasesUrl ? (
            <a
              href={about.releasesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Releases
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : null}
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          If another Windows user is signed in, the tray icon may only appear on the account that launched SDTestApp.
          You can still exit from here when connected locally.
        </p>
        {!observeOnly && canActuate ? (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={updateCheckPending || exitPending}
                onClick={() => {
                  void (async () => {
                    setUpdateCheckPending(true);
                    setUpdateCheckMessage(null);
                    setError(null);
                    try {
                      const result = await client.checkForUpdates();
                      setUpdateCheckMessage(
                        result.message || "Checking for updates… A Windows dialog may appear on this bench.",
                      );
                    } catch (updateError) {
                      setError(updateError instanceof Error ? updateError.message : String(updateError));
                    } finally {
                      setUpdateCheckPending(false);
                    }
                  })();
                }}
              >
                {updateCheckPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1 h-4 w-4" />
                )}
                Check for Updates
              </Button>
              {!exitConfirmOpen ? (
                <Button type="button" variant="secondary" size="sm" onClick={() => setExitConfirmOpen(true)}>
                  <Power className="mr-1 h-4 w-4" />
                  Exit SDTestApp…
                </Button>
              ) : null}
            </div>
            {updateCheckMessage ? (
              <p className="text-sm text-muted-foreground">{updateCheckMessage}</p>
            ) : null}
            {exitConfirmOpen ? (
              <div className="rounded border border-destructive/40 bg-destructive/5 p-3">
                <p className="text-sm font-medium">Exit SDTestApp?</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  This shuts down the bench engine on this PC. Remote Hub users will lose local actuation until SDTestApp
                  is started again.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    disabled={exitPending}
                    onClick={() => {
                      void (async () => {
                        setExitPending(true);
                        setError(null);
                        try {
                          await client.exitApplication();
                        } catch (exitError) {
                          setError(exitError instanceof Error ? exitError.message : String(exitError));
                          setExitPending(false);
                          setExitConfirmOpen(false);
                        }
                      })();
                    }}
                  >
                    {exitPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Exit SDTestApp"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={exitPending}
                    onClick={() => setExitConfirmOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      <p className="text-sm text-muted-foreground">
        SDTestApp INI settings from CONFIG_REGISTRY. Restart-required fields are marked and persist without changing the live session.
      </p>
      {!canActuate && !observeOnly ? (
        <p className="text-sm text-amber-800">Settings edits require local SDTestApp actuation on this bench PC.</p>
      ) : observeOnly ? (
        <p className="text-sm text-muted-foreground">Read-only settings view via relay observe.</p>
      ) : null}
      {sections.map(([section, sectionFields]) => (
        <section key={section} className="rounded border p-4">
          <h3 className="mb-3 font-semibold">{section}</h3>
          <div className="space-y-3">
            {sectionFields.map((field) => (
              <div key={field.name} className="grid gap-2 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center">
                <div>
                  <div className="text-sm font-medium">{field.label}</div>
                  <div className="font-mono text-xs text-muted-foreground">{field.name}</div>
                </div>
                <Input
                  value={drafts[field.name] ?? ""}
                  disabled={!canActuate || observeOnly}
                  onChange={(event) => setDrafts((current) => ({ ...current, [field.name]: event.target.value }))}
                />
                <div className="flex items-center gap-2">
                  {field.requiresRestart ? <Badge tone="warning">Restart</Badge> : null}
                  {!observeOnly ? (
                  <Button
                    type="button"
                    size="sm"
                    disabled={
                      !canActuate ||
                      pendingField === field.name ||
                      (drafts[field.name] ?? "") === (field.value == null ? "" : String(field.value))
                    }
                    onClick={() => {
                      void (async () => {
                        setPendingField(field.name);
                        setError(null);
                        try {
                          const raw = drafts[field.name] ?? "";
                          let parsed: unknown = raw;
                          if (field.type === "bool") parsed = ["1", "true", "yes", "on"].includes(raw.trim().toLowerCase());
                          else if (field.type === "int") parsed = Number.parseInt(raw, 10);
                          else if (field.type === "float") parsed = Number.parseFloat(raw);
                          else if (field.type === "list") parsed = raw.split(",").map((part) => part.trim()).filter(Boolean);
                          await client.saveSetting(field.name, parsed);
                          setFields((current) =>
                            current.map((existing) =>
                              existing.name === field.name ? { ...existing, value: parsed } : existing,
                            ),
                          );
                        } catch (saveError) {
                          setError(saveError instanceof Error ? saveError.message : String(saveError));
                        } finally {
                          setPendingField(null);
                        }
                      })();
                    }}
                  >
                    {pendingField === field.name ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
